// MUNYA OS · C2/C2b · Integración con el wrapper nativo iOS (Capacitor)
//
// La web se carga REMOTA en el WebView (server.url), así que no importamos @capacitor/*:
// el bridge nativo inyecta `window.Capacitor` con los plugins ya registrados. En navegador
// normal todo esto es un no-op silencioso.

import { navigate } from './router.js';

interface CapacitorGlobal {
  isNativePlatform?: () => boolean;
  getPlatform?: () => string;
  Plugins?: {
    PushNotifications?: {
      checkPermissions: () => Promise<{ receive: string }>;
      requestPermissions: () => Promise<{ receive: string }>;
      register: () => Promise<void>;
      addListener: (event: string, cb: (data: unknown) => void) => void;
    };
    // @aparajita/capacitor-biometric-auth · el plugin NATIVO se registra como
    // BiometricAuthNative y expone internalAuthenticate (el alias `authenticate`
    // solo existe en el wrapper npm, que aquí no cargamos al ser web remota).
    BiometricAuthNative?: {
      checkBiometry: () => Promise<{ isAvailable: boolean; biometryType: number }>;
      internalAuthenticate: (opts: {
        reason: string;
        cancelTitle?: string;
        allowDeviceCredential?: boolean;
        iosFallbackTitle?: string;
      }) => Promise<void>;
    };
    Geolocation?: {
      getCurrentPosition: (opts?: {
        enableHighAccuracy?: boolean;
        timeout?: number;
        maximumAge?: number;
      }) => Promise<{ coords: { latitude: number; longitude: number; accuracy: number } }>;
    };
  };
}

function cap(): CapacitorGlobal | undefined {
  return (window as { Capacitor?: CapacitorGlobal }).Capacitor;
}

/** true solo dentro de la app iOS/Android (WebView Capacitor). */
export function isNativeApp(): boolean {
  return cap()?.isNativePlatform?.() === true;
}

const TOKEN_KEY = 'munya-push-token';
let pushInitDone = false;

/**
 * Registro de push: pedir permiso (una vez), registrar en APNs y enviar el token a la API.
 * Llamar tras confirmar sesión (el POST requiere cookie). Idempotente.
 */
export async function initNativePush(): Promise<void> {
  if (pushInitDone || !isNativeApp()) return;
  const pn = cap()?.Plugins?.PushNotifications;
  if (!pn) return;
  pushInitDone = true;

  pn.addListener('registration', (data) => {
    const token = (data as { value?: string })?.value;
    if (!token) return;
    try {
      localStorage.setItem(TOKEN_KEY, token); // para la baja en el logout
    } catch {
      /* storage lleno/privado: la baja simplemente no encontrará token */
    }
    void fetch('/api/v1/native/device-token', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ token, platform: cap()?.getPlatform?.() ?? 'ios' }),
    }).catch((e) => console.warn('[munya-push] registro del token falló', e));
  });

  pn.addListener('registrationError', (err) => {
    console.warn('[munya-push] registrationError', JSON.stringify(err));
  });

  // Tap en una push → navegar al destino de la notificación (cta_url del payload)
  pn.addListener('pushNotificationActionPerformed', (ev) => {
    const url = (ev as { notification?: { data?: { cta_url?: string } } })?.notification?.data
      ?.cta_url;
    if (url && url.startsWith('/')) navigate(url);
  });

  try {
    let status = (await pn.checkPermissions()).receive;
    if (status === 'prompt' || status === 'prompt-with-rationale') {
      status = (await pn.requestPermissions()).receive;
    }
    if (status === 'granted') await pn.register();
  } catch (e) {
    console.warn('[munya-push] init falló', e);
  }
}

/**
 * Abre un documento de la API (PDF de factura, honorarios, gasto, export…).
 * En navegador, la pestaña nueva de siempre. En la app, window.open lanza SAFARI,
 * que no comparte la cookie de sesión del WebView → 401: navegamos dentro del
 * propio WebView (misma cookie; los endpoints sirven Content-Disposition: inline
 * y el visor nativo renderiza el PDF, con "atrás" para volver).
 */
export function openDocument(url: string): void {
  if (isNativeApp()) {
    window.location.assign(url);
  } else {
    window.open(url, '_blank', 'noopener');
  }
}

/**
 * C2b · Baja del token de push al cerrar sesión desde la app. keepalive para que la
 * petición sobreviva a la navegación a /auth/logout. No-op fuera de la app.
 */
export function unregisterNativeToken(): void {
  if (!isNativeApp()) return;
  let token: string | null = null;
  try {
    token = localStorage.getItem(TOKEN_KEY);
    localStorage.removeItem(TOKEN_KEY);
  } catch {
    return;
  }
  if (!token) return;
  void fetch('/api/v1/native/device-token', {
    method: 'DELETE',
    credentials: 'same-origin',
    keepalive: true,
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ token }),
  }).catch(() => undefined);
}

/**
 * C2b · Confirmación biométrica (Face ID / Touch ID) antes de fichar, SOLO en la app.
 * · Web o dispositivo sin biometría → true (no bloquea; la app añade la capa, no la exige).
 * · Cancelación o fallo de identidad → false (se aborta el fichaje).
 * allowDeviceCredential: si Face ID falla, iOS ofrece el código del dispositivo (identidad
 * verificada igualmente — manos mojadas no impiden fichar).
 */
export async function confirmBiometric(reason: string): Promise<boolean> {
  if (!isNativeApp()) return true;
  const bio = cap()?.Plugins?.BiometricAuthNative;
  if (!bio) return true;
  try {
    const { isAvailable } = await bio.checkBiometry();
    if (!isAvailable) return true;
  } catch {
    return true;
  }
  try {
    await bio.internalAuthenticate({
      reason,
      cancelTitle: 'Cancelar',
      allowDeviceCredential: true,
      iosFallbackTitle: 'Usar código',
    });
    return true;
  } catch {
    return false; // cancelado o no verificado
  }
}

/**
 * C2b · Posición actual para el fichaje, SOLO en la app y best-effort (5s, sin alta
 * precisión). El backend la descarta si el usuario no tiene el opt-in L5 activo, pero
 * solo se llama con consentimiento para no pedir el permiso de ubicación en vano.
 */
export async function captureLocation(): Promise<
  { location_lat: number; location_lng: number } | undefined
> {
  if (!isNativeApp()) return undefined;
  const geo = cap()?.Plugins?.Geolocation;
  if (!geo) return undefined;
  try {
    const pos = await geo.getCurrentPosition({
      enableHighAccuracy: false,
      timeout: 5000,
      maximumAge: 60_000,
    });
    return { location_lat: pos.coords.latitude, location_lng: pos.coords.longitude };
  } catch {
    return undefined; // sin permiso o sin señal: el fichaje sigue sin coords
  }
}
